import { type JSX, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbar, type GridColDef, type GridRowParams } from '@mui/x-data-grid';

import { listTelemetryEvents, type PortalTelemetryEvent } from '../../api.js';

const REFRESH_MS = 30_000;
const TELEMETRY_CATEGORIES = ['', 'tileLaunch', 'profileSwitch', 'configError', 'licenseError', 'agentAction', 'remoteConfigPoll', 'oem'];

const EVENT_LABELS: Record<string, string> = {
  'tileLaunch:app': 'App launched',
  'tileLaunch:web': 'Web tile opened',
  'profileSwitch:': 'Profile switched',
  'admin.exitGesture': 'Admin exit gesture',
  'admin.pinAccepted': 'Admin PIN accepted',
  'admin.pinRejected': 'Admin PIN rejected',
  'admin.overlayOpened': 'Diagnostic overlay opened',
  'admin.overlayClosed': 'Diagnostic overlay closed',
};

function relTime(ms: number): string {
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return `${String(s)}s ago`;
  if (s < 3600) return `${String(Math.round(s / 60))}m ago`;
  if (s < 86400) return `${String(Math.round(s / 3600))}h ago`;
  return new Date(ms).toLocaleDateString();
}
function attrsOf(e: PortalTelemetryEvent): Record<string, unknown> {
  return e.attrs && typeof e.attrs === 'object' ? (e.attrs as Record<string, unknown>) : {};
}
function deviceOf(e: PortalTelemetryEvent): string {
  const a = attrsOf(e);
  const id = typeof a['deviceId'] === 'string' ? a['deviceId'] : '';
  return id.length > 14 ? `${id.slice(0, 12)}…` : id;
}
function summaryOf(e: PortalTelemetryEvent): string {
  const a = { ...attrsOf(e) };
  delete a['deviceId'];
  delete a['at'];
  const s = JSON.stringify(a);
  return s === '{}' ? '' : s;
}

/**
 * Shared events grid for the Telemetry and Audit sections — both read the same
 * telemetry endpoint (audit is just `category: 'audit'`). MUI DataGrid gives
 * sort / quick-filter / CSV-export out of the box; a row click shows the raw
 * event JSON. Auto-refreshes every 30s.
 */
export function EventsSection({
  tenantId,
  editToken,
  currentTier,
  mode,
}: {
  tenantId: string;
  editToken: string;
  currentTier: 'core' | 'pro' | 'enterprise';
  mode: 'telemetry' | 'audit';
}): JSX.Element {
  const [events, setEvents] = useState<PortalTelemetryEvent[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'unauthorized' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [detail, setDetail] = useState<PortalTelemetryEvent | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    let stop = false;
    const id = ++seq.current;
    async function load(): Promise<void> {
      setStatus((s) => (s === 'ok' ? 'ok' : 'loading'));
      const res = await listTelemetryEvents({
        tenantId,
        editToken,
        limit: mode === 'audit' ? 200 : 100,
        ...(mode === 'audit' ? { category: 'audit' } : category ? { category } : {}),
      });
      if (stop || id !== seq.current) return;
      if (res.kind === 'ok') {
        setEvents(res.events);
        setStatus('ok');
      } else if (res.kind === 'unauthorized') {
        setStatus('unauthorized');
      } else {
        setStatus('error');
        setErrMsg(res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.`);
      }
    }
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [tenantId, editToken, mode, category]);

  const columns: GridColDef<PortalTelemetryEvent>[] = [
    { field: 'received', headerName: 'When', width: 110, valueGetter: (_v, r) => relTime(r.receivedAt), sortComparator: () => 0 },
    ...(mode === 'telemetry' ? [{ field: 'category', headerName: 'Category', width: 140 } as GridColDef<PortalTelemetryEvent>] : []),
    {
      field: 'name',
      headerName: mode === 'audit' ? 'Action' : 'Event',
      width: 200,
      valueGetter: (_v, r) => EVENT_LABELS[`${r.category}:${r.name}`] ?? EVENT_LABELS[r.name] ?? r.name,
    },
    { field: 'device', headerName: 'Device', width: 150, valueGetter: (_v, r) => deviceOf(r) },
    { field: 'summary', headerName: 'Summary', flex: 1, minWidth: 200, valueGetter: (_v, r) => summaryOf(r) },
  ];

  const title = mode === 'audit' ? 'Audit log' : 'Telemetry';
  const sub =
    mode === 'audit'
      ? 'Tamper-evident record of admin actions, gestures, PIN events, and runtime state changes.'
      : 'Events your fleet reports to the hosted ingest endpoint. Refreshes every 30 seconds.';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h5" fontWeight={700}>{title}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>{sub}</Typography>

      {currentTier !== 'enterprise' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {mode === 'audit' ? 'The audit log' : 'Telemetry export'} is an Enterprise feature — Core/Pro devices don’t flush events to the hosted endpoint.
        </Alert>
      )}
      {status === 'unauthorized' && <Alert severity="error" sx={{ mb: 2 }}>Edit token rejected — reopen this fleet from the switcher.</Alert>}
      {status === 'error' && errMsg !== null && <Alert severity="error" sx={{ mb: 2 }}>{errMsg}</Alert>}

      {mode === 'telemetry' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 180 }}>
            {TELEMETRY_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c === '' ? 'All categories' : c}</MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 360, minWidth: 0 }}>
        <DataGrid
          rows={events}
          columns={columns}
          loading={status === 'loading'}
          density="compact"
          disableRowSelectionOnClick
          onRowClick={(p: GridRowParams<PortalTelemetryEvent>) => setDetail(p.row)}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true, csvOptions: { fileName: `mobilauncher-${mode}-${tenantId}` } } }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      <Dialog open={detail !== null} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail ? (EVENT_LABELS[`${detail.category}:${detail.name}`] ?? detail.name) : ''}</DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{ m: 0, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {detail ? JSON.stringify({ category: detail.category, name: detail.name, ...attrsOf(detail) }, null, 2) : ''}
          </Box>
          <Button sx={{ mt: 2 }} onClick={() => setDetail(null)}>Close</Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
